import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Building2, MapPin, Wifi, Euro, Clock, ExternalLink,
  Briefcase, Code2, CalendarDays, AlertCircle, Loader2, Lock, Unlock,
  Upload, FileText, CheckCircle2, RotateCcw, X, Zap, History, Coins, AlertTriangle, Mail
} from 'lucide-react';
import api from '../api/api';
import useAuth from '../hooks/useAuth';
import styles from './JobDetailsPage.module.css';
import MatchingResult from './MatchingResult';
import DOMPurify from 'dompurify';

// ─────────────────────────────────────────────────────────────
// CONSTANTES & HELPERS
// ─────────────────────────────────────────────────────────────
const POLLING_TIMEOUT_MS = 10 * 60 * 1000;
const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const ALLOWED_EXTENSIONS_STRING = '.pdf,.doc,.docx';

// ── Les grandes étapes réelles du backend ──
const MAIN_STEPS = [
  { key: 'downloading', text: "Téléchargement sécurisé des documents..." },
  { key: 'extracting',  text: "Extraction du texte et traitement OCR..." },
  { key: 'vectorizing', text: "Calcul des embeddings et similarité sémantique..." },
  { key: 'ai_analysis', text: "Analyse IA en cours..." }
];

// ── Les sous-étapes simulées pour faire patienter pendant 'ai_analysis' ──
const AI_STEPS = [
  "Le LLM analyse le contexte global de l'offre...",
  "Extraction des compétences MUST-HAVE et NICE-TO-HAVE...",
  "Évaluation de l'expertise sectorielle du candidat...",
  "Vérification de la stabilité et de la trajectoire...",
  "Analyse des risques (Flight risk, Ramp-up)...",
  "Génération de l'argumentaire commercial...",
  "Préparation des questions d'entretien...",
  "Calcul des scores finaux et synthèse...",
  "Finalisation du rapport..."
];

// ─────────────────────────────────────────────────────────────
// SKELETON INITIAL (PAGE)
// ─────────────────────────────────────────────────────────────
const DetailSkeleton = () => (
  <div className={styles.page}>
    <div className={`${styles.skeletonLine} ${styles.skeletonBack}`} />
    <div className={styles.heroCard}>
      <div className={styles.skeletonHeroTop}>
        <div className={styles.skeletonHeroLeft}>
          <div className={`${styles.skeletonLine} ${styles.skeletonHeroTitle}`} />
          <div className={`${styles.skeletonLine} ${styles.skeletonHeroCompany}`} />
        </div>
        <div className={`${styles.skeletonLine} ${styles.skeletonHeroBtn}`} />
      </div>
      <div className={styles.skeletonBadges}>
        {[120, 100, 140, 110].map((w, i) => (
          <div key={i} className={`${styles.skeletonLine} ${styles.skeletonBadge}`} style={{ width: w }} />
        ))}
      </div>
    </div>
    <div className={styles.contentCard}>
      <div className={`${styles.skeletonLine} ${styles.skeletonSectionTitle}`} />
      {[100, 95, 88, 100, 72, 90].map((w, i) => (
        <div key={i} className={`${styles.skeletonLine} ${styles.skeletonBodyLine}`} style={{ width: `${w}%` }} />
      ))}
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// BADGE
// ─────────────────────────────────────────────────────────────
const Badge = ({ icon: Icon, text, variant = 'default' }) => (
  <span className={`${styles.badge} ${styles[`badge_${variant}`]}`}>
    <Icon size={14} strokeWidth={2} />
    {text}
  </span>
);

// ─────────────────────────────────────────────────────────────
// COMPOSANT PRINCIPAL
// ─────────────────────────────────────────────────────────────
const JobDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refreshToken, quota, user } = useAuth();

  // États pour les détails de l'offre
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // États pour le Matching In-Situ
  const [cvFile, setCvFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [matchStatus, setMatchStatus] = useState('idle'); // idle | processing | completed | error
  const [taskId, setTaskId] = useState(null);
  const [matchResult, setMatchResult] = useState(null);
  const [matchError, setMatchError] = useState('');
  
  // États pour le chargement hybride (Catch-up system)
  const [backendStep, setBackendStep] = useState('downloading');
  const [visualStepIndex, setVisualStepIndex] = useState(0);
  const [aiSubStep, setAiSubStep] = useState(0);
  
  // État pour l'historique spécifique à cette offre
  const [jobHistory, setJobHistory] = useState([]);

  const pollingInterval = useRef(null);
  const cvInputRef = useRef(null);
  const startTimeRef = useRef(null);

  const [isPrivate, setIsPrivate] = useState(false);
  const [showRawEmail, setShowRawEmail] = useState(false);

  const hasMatchingAccess = user?.permissions?.matching ?? true;

  // ── 1. Chargement de l'offre & de l'historique ──
  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        const response = await api.get(`/jobs/${id}`);
        setJob(response.data);
        setIsPrivate(response.data.is_private || false); // <--- NOUVEAU
      } catch (err) {
        setError(err.response?.data?.error || "Erreur lors de la récupération des détails de l'offre.");
      } finally {
        setLoading(false);
      }
    };
    fetchJobDetails();
  }, [id]);

  // ── Chargement de l'historique spécifique ──
  useEffect(() => {
    const fetchJobSpecificHistory = async () => {
      try {
        const res = await api.get('/matching/history', {
          params: {
            job_prefix: `AO_${id}_`,
            per_page: 10
          }
        });
        setJobHistory(res.data.items || []); 
      } catch (err) {
        console.error("Erreur récupération historique de l'offre", err);
      }
    };

    if (hasMatchingAccess) {
      fetchJobSpecificHistory();
    }
  }, [id, hasMatchingAccess]);

  const handleTogglePrivacy = async () => {
    try {
        const newValue = !isPrivate;
        await api.put(`/jobs/${id}/privacy`, { is_private: newValue });
        setIsPrivate(newValue);
    } catch (err) {
        alert(err.response?.data?.error || "Erreur lors de la modification de la visibilité.");
    }
  };

  // ── 2. Logique de Matching (Fichier virtuel + Polling) ──
  const updateStartTime = useCallback((time) => {
    startTimeRef.current = time;
  }, []);

  const stopProcessing = useCallback((stopStatus, message = '') => {
    if (pollingInterval.current) clearInterval(pollingInterval.current);
    localStorage.removeItem(`jobMatchingTask_${id}`);
    setMatchStatus(stopStatus);
    if (message) setMatchError(message);
    updateStartTime(null);
    
    // Réinitialisation des étapes hybrides
    setBackendStep('downloading');
    setVisualStepIndex(0);
    setAiSubStep(0);
  }, [updateStartTime, id]);

  const checkTaskStatus = useCallback(async (currentTaskId, isRecovery = false) => {
    if (!currentTaskId) return;
    try {
      const response = await api.get(`/status/${currentTaskId}`);
      const data = response.data;
      if (data.status === 'completed') {
        setMatchResult(data);
        setJobHistory(prev => [data, ...prev.filter(item => item.id !== data.id)].slice(0, 10));
        stopProcessing('completed');
      } else if (data.status === 'error') {
        stopProcessing('error', data.message || 'Une erreur est survenue durant le matching.');
      } else if (data.status === 'processing') {
        // MISE À JOUR DE LA RÉALITÉ DU BACKEND
        if (data.progress_step) {
          setBackendStep(data.progress_step);
        }
      } else {
        const startTime = startTimeRef.current;
        if (startTime && Date.now() - startTime > POLLING_TIMEOUT_MS) {
          stopProcessing('error', 'Le traitement a dépassé 10 minutes et a été arrêté.');
        }
      }
    } catch {
      if (!isRecovery) stopProcessing('error', 'Erreur de connexion lors de la vérification du statut.');
    }
  }, [stopProcessing]);

  const startPolling = useCallback((currentTaskId) => {
    if (pollingInterval.current) clearInterval(pollingInterval.current);
    pollingInterval.current = setInterval(() => {
      checkTaskStatus(currentTaskId, false);
    }, 10000);
  }, [checkTaskStatus]);

  // ── EFFET 1 : LE RATTRAPAGE VISUEL (CATCH-UP) ──
  useEffect(() => {
    if (matchStatus !== 'processing') return;

    const targetIndex = MAIN_STEPS.findIndex(s => s.key === backendStep);
    
    if (visualStepIndex < targetIndex) {
      const timer = setTimeout(() => {
        setVisualStepIndex(prev => prev + 1);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [matchStatus, backendStep, visualStepIndex]);

  // ── EFFET 2 : L'ANIMATION PENDANT L'IA ──
  useEffect(() => {
    if (matchStatus !== 'processing') return;

    if (MAIN_STEPS[visualStepIndex]?.key === 'ai_analysis') {
      const timer = setInterval(() => {
        setAiSubStep((prev) => (prev < AI_STEPS.length - 1 ? prev + 1 : prev));
      }, 12000);
      return () => clearInterval(timer);
    }
  }, [matchStatus, visualStepIndex]);

  // ── 3. Reprise de session (localStorage) ──
  useEffect(() => {
    const savedTaskJSON = localStorage.getItem(`jobMatchingTask_${id}`);
    if (savedTaskJSON) {
      try {
        const savedTask = JSON.parse(savedTaskJSON);
        if (savedTask?.id && savedTask.status === 'processing') {
          setTaskId(savedTask.id);
          setMatchStatus('processing');
          if (savedTask.startTime) updateStartTime(savedTask.startTime);
          if (savedTask.fileName) setCvFile({ name: savedTask.fileName });
          
          checkTaskStatus(savedTask.id, true).then(() => {
            const startTime = startTimeRef.current;
            const isTimeout = startTime && Date.now() - startTime > POLLING_TIMEOUT_MS;
            if (!isTimeout) startPolling(savedTask.id);
          });
        } else {
          localStorage.removeItem(`jobMatchingTask_${id}`);
        }
      } catch {
        localStorage.removeItem(`jobMatchingTask_${id}`);
      }
    }
    return () => {
      if (pollingInterval.current) clearInterval(pollingInterval.current);
    };
  }, [id, checkTaskStatus, startPolling, updateStartTime]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && matchStatus === 'processing' && taskId) {
        checkTaskStatus(taskId, false);
        startPolling(taskId);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [matchStatus, taskId, checkTaskStatus, startPolling]);

  // ── 4. Gestion des fichiers CV ──
  const handleCvChange = (file) => {
    if (file && ALLOWED_MIME_TYPES.includes(file.type)) {
      setCvFile(file);
      setMatchError('');
    } else {
      setCvFile(null);
      setMatchError(`Fichier CV invalide. Formats acceptés : PDF, DOC, DOCX.`);
      if (cvInputRef.current) cvInputRef.current.value = null;
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleCvChange(file);
  };

  // ── 5. Lancement du Matching ──
  const startMatching = async (e) => {
    e.preventDefault();
    if (!cvFile) {
      setMatchError('Veuillez déposer un CV.');
      return;
    }

    setMatchStatus('processing');
    setMatchError('');
    setBackendStep('downloading');
    setVisualStepIndex(0);
    setAiSubStep(0);

    const jobTextContent = `
Titre du poste: ${job.title}
Entreprise: ${job.company}
Lieu: ${job.location}
Télétravail: ${job.remote_mode}
Expérience requise: ${job.experience}

Description de la mission:
${job.description}

Compétences attendues:
${job.skills && job.skills.length > 0 ? job.skills.join(', ') : 'Non spécifiées'}
    `.trim();

    const jobBlob = new Blob([jobTextContent], { type: 'text/plain' });
    const fakeJobFile = new File([jobBlob], `AO_${job.id}_${job.company.replace(/\s+/g, '_')}.txt`, { type: 'text/plain' });

    const formData = new FormData();
    formData.append('file1', cvFile);
    formData.append('file2', fakeJobFile);

    try {
      const response = await api.post('/matching/process', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      const { task_id, new_token } = response.data;
      if (new_token) refreshToken(new_token);
      
      const startTime = Date.now();
      setTaskId(task_id);
      updateStartTime(startTime);
      
      localStorage.setItem(`jobMatchingTask_${id}`, JSON.stringify({
        id: task_id,
        status: 'processing',
        fileName: cvFile.name,
        startTime
      }));

      startPolling(task_id);
    } catch (error) {
      setMatchStatus('error');
      setMatchError(error.response?.data?.error || "Erreur lors de l'envoi des fichiers.");
    }
  };

  const resetMatching = () => {
    setCvFile(null);
    if (cvInputRef.current) cvInputRef.current.value = null;
    stopProcessing('idle');
    setTaskId(null);
    setMatchResult(null);
  };

  if (loading) return <DetailSkeleton />;

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.errorBox}>
          <AlertCircle size={18} strokeWidth={2} />
          {error}
        </div>
        <button className={styles.backBtn} onClick={() => navigate('/job-search')}>
          <ArrowLeft size={16} strokeWidth={2} /> Retour à la recherche
        </button>
      </div>
    );
  }

  if (!job) return null;

  const sourceName = job.source === 'FranceTravail' ? 'France Travail' : job.source;

  return (
    <div className={styles.page}>
      
      {/* ── BARRE SUPÉRIEURE (Retour & Quota) ── */}
      <div className={styles.topBar}>
        <button className={styles.backBtn} onClick={() => navigate('/job-search')}>
          <ArrowLeft size={16} strokeWidth={2} />
          Retour à la recherche
        </button>

        {quota !== null && quota !== undefined && (
          <div className={styles.quotaBadge}>
            <Coins size={14} strokeWidth={2} />
            <span><strong>{quota}</strong> crédit{quota > 1 ? 's' : ''}</span>
          </div>
        )}
      </div>

      {/* ══ CARTE HERO ══ */}
      <div className={styles.heroCard}>
        <div className={styles.heroAccent} aria-hidden="true" />
        <div className={styles.heroTop}>
          <div className={styles.heroLeft}>
            <div className={styles.companyIcon}>
              <Briefcase size={26} strokeWidth={1.75} />
            </div>
            <div className={styles.heroTitles}>
              <h1 className={styles.jobTitle}>{job.title}</h1>
              <div className={styles.companyRow}>
                <Building2 size={15} strokeWidth={2} />
                <span className={styles.companyName}>{job.company}</span>
              </div>
            </div>
          </div>
          {job.url && (
            <a href={job.url} target="_blank" rel="noopener noreferrer" className={styles.applyBtn}>
              <ExternalLink size={16} strokeWidth={2.5} /> Voir sur {sourceName}
            </a>
          )}
        </div>
        {user?.type === 'Admin' && (
            <button onClick={handleTogglePrivacy} className={styles.applyBtn} style={{ backgroundColor: isPrivate ? '#10b981' : '#f59e0b', color: 'white', border: 'none', marginLeft: '10px' }}>
                {isPrivate ? <><Unlock size={16} strokeWidth={2.5} /> Rendre Publique</> : <><Lock size={16} strokeWidth={2.5} /> Rendre Privée</>}
            </button>
        )}
        <div className={styles.badgeRow}>
          {isPrivate && <Badge icon={Lock} text="Offre Privée" variant="warning" />}
          <Badge icon={MapPin} text={job.location} variant="default" />
          {job.remote_mode && job.remote_mode !== 'Non précisé' && <Badge icon={Wifi} text={job.remote_mode} variant="info" />}
          {job.salary && job.salary !== 'Non communiqué' && <Badge icon={Euro} text={job.salary} variant="success" />}
          {job.experience && job.experience !== 'Non précisée' && <Badge icon={Clock} text={`Expérience : ${job.experience}`} variant="warning" />}
        </div>
      </div>

      {/* ══ CARTE CONTENU PRINCIPAL ══ */}
      <div className={styles.contentCard}>
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionIconWrap}><Briefcase size={16} strokeWidth={2} /></div>
            <h2 className={styles.sectionTitle}>Détails de la mission</h2>
          </div>
          <div className={styles.description}>{job.description}</div>

          {job.source === "INOP'S" && job.raw_description && (
            <div style={{ marginTop: '20px' }}>
              <button 
                onClick={() => setShowRawEmail(!showRawEmail)}
                className={styles.actionBtnLight}
                style={{ width: '100%', display: 'flex', justifyContent: 'center', gap: '8px', padding: '12px' }}
              >
                <Mail size={16} strokeWidth={2} /> 
                {showRawEmail ? "Masquer l'e-mail original" : "Consulter l'e-mail original complet"}
              </button>
              
              {showRawEmail && (
                <div 
                  style={{ 
                    marginTop: '12px', 
                    padding: '16px', 
                    backgroundColor: '#ffffff', // Fond blanc pour le mail
                    borderRadius: '8px', 
                    border: '1px solid var(--color-border)',
                    fontSize: '0.9rem',
                    maxHeight: '500px',
                    overflowY: 'auto'
                  }}
                  // On nettoie le HTML ici
                  dangerouslySetInnerHTML={{ 
                    __html: DOMPurify.sanitize(job.raw_description) 
                  }} 
                />
              )}
            </div>
          )}
        </section>

        {job.skills && job.skills.length > 0 && (
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionIconWrap}><Code2 size={16} strokeWidth={2} /></div>
              <h2 className={styles.sectionTitle}>Compétences requises</h2>
            </div>
            <div className={styles.skillList}>
              {job.skills.map((skill, i) => (
                <span key={i} className={styles.skillTag}>{skill}</span>
              ))}
            </div>
          </section>
        )}

        {/* ══ SECTION : TESTER MON CANDIDAT (MATCHING IN-SITU) ══ */}
        {hasMatchingAccess && (
          <section className={`${styles.section} ${styles.matchingSection}`}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionIconWrap}><Zap size={16} strokeWidth={2} /></div>
              <h2 className={styles.sectionTitle}>Évaluer un profil pour cette mission</h2>
            </div>

            {/* ÉTAT 1 : IDLE (Dépôt du CV) */}
            {matchStatus === 'idle' && (
              <>
                <form onSubmit={startMatching} className={styles.matchingForm}>
                  <div 
                    className={`${styles.dropZone} ${cvFile ? styles.dropZoneActive : ''} ${isDragging ? styles.dropZoneDragging : ''}`}
                    onClick={() => cvInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={handleDrop}
                    role="button" tabIndex={0}
                  >
                    <input type="file" hidden ref={cvInputRef} accept={ALLOWED_EXTENSIONS_STRING} onChange={(e) => handleCvChange(e.target.files[0])} />
                    
                    {cvFile && (
                      <button type="button" className={styles.removeBtn} onClick={(e) => { e.stopPropagation(); setCvFile(null); if (cvInputRef.current) cvInputRef.current.value = null; }}>
                        <X size={13} strokeWidth={2.5} />
                      </button>
                    )}

                    {cvFile ? (
                      <div className={styles.fileInfo}>
                        <FileText size={28} strokeWidth={1.5} className={styles.fileIcon} />
                        <p className={styles.fileLabel}>CV prêt à être analysé</p>
                        <p className={styles.fileName}>{cvFile.name}</p>
                      </div>
                    ) : (
                      <div className={styles.emptyZone}>
                        <Upload size={26} strokeWidth={1.5} className={styles.uploadIcon} />
                        <p className={styles.dropLabel}>Déposez le CV de votre candidat</p>
                        <p className={styles.dropHint}>Glissez ou <span>parcourez</span></p>
                      </div>
                    )}
                  </div>

                  {matchError && (
                    <div className={styles.errorBox}>
                      <AlertTriangle size={15} strokeWidth={2} /> {matchError}
                    </div>
                  )}

                  <div className={styles.submitRow}>
                    <button type="submit" className={styles.submitMatchBtn} disabled={!cvFile}>
                      <Zap size={16} strokeWidth={2} />
                      Lancer le Matching (1 crédit)
                    </button>
                  </div>
                </form>

                {/* ══ SOUS-SECTION : ANALYSES PRÉCÉDENTES POUR CETTE OFFRE ══ */}
                {jobHistory.length > 0 && (
                  <div className={styles.historyContainer}>
                    <h3 className={styles.historyContainerTitle}>
                      <History size={14} strokeWidth={2.5} /> Analyses précédentes
                    </h3>
                    <div className={styles.historyList}>
                      {jobHistory.map((item) => (
                        <div key={item.id} className={styles.historyMiniCard}>
                          <div className={styles.historyMiniLeft}>
                            <FileText size={18} strokeWidth={1.5} className={styles.historyMiniIcon} />
                            <div className={styles.historyMiniDetails}>
                              <span className={styles.historyMiniName}>{item.candidate_name || item.file1_filename || 'Candidat'}</span>
                              <span className={styles.historyMiniDate}>
                                {item.created_at ? new Date(item.created_at).toLocaleDateString('fr-FR') : "Récemment"}
                              </span>
                            </div>
                          </div>
                          <div className={styles.historyMiniRight}>
                            <span className={`${styles.historyScoreBadge} ${
                              item.global_score >= 75 ? styles.scoreHigh : item.global_score >= 50 ? styles.scoreMedium : styles.scoreLow
                            }`}>
                              {item.global_score}%
                            </span>
                            <button 
                              className={styles.actionBtnLight} 
                              onClick={() => {
                                setMatchResult(item);
                                setMatchStatus('completed');
                              }}
                            >
                              Revoir
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ÉTAT 2 : PROCESSING - THE SKELETON PREVIEW */}
            {matchStatus === 'processing' && (
              <div className={styles.skeletonSection}>
                {/* Header d'activité en temps réel */}
                <div className={styles.skeletonLiveHeader}>
                  <div className={styles.skeletonLiveIcon}>
                    <Loader2 className={styles.spin} size={20} />
                  </div>
                  <div className={styles.skeletonLiveText}>
                    <h4>Analyse en cours</h4>
                    <p className={styles.skeletonStepMessage}>
                      {MAIN_STEPS[visualStepIndex]?.key === 'ai_analysis' 
                        ? AI_STEPS[aiSubStep] 
                        : MAIN_STEPS[visualStepIndex]?.text}
                    </p>
                  </div>
                </div>

                {/* Le Skeleton Grid statique */}
                <div className={styles.skeletonGrid}>
                  <div className={styles.skeletonScoresCol}>
                    <div className={`${styles.shimmer} ${styles.skeletonGauge}`} />
                    <div className={`${styles.shimmer} ${styles.skeletonLine}`} style={{ width: '80%', height: '24px', marginTop: '16px' }} />
                  </div>
                  
                  <div className={styles.skeletonSummaryCol}>
                    <div className={`${styles.shimmer} ${styles.skeletonLine}`} style={{ width: '50%', height: '28px', marginBottom: '24px' }} />
                    <div className={`${styles.shimmer} ${styles.skeletonLine}`} style={{ width: '100%', marginBottom: '12px' }} />
                    <div className={`${styles.shimmer} ${styles.skeletonLine}`} style={{ width: '100%', marginBottom: '12px' }} />
                    <div className={`${styles.shimmer} ${styles.skeletonLine}`} style={{ width: '70%', marginBottom: '24px' }} />
                    
                    <div className={styles.skeletonSwot}>
                      <div className={`${styles.shimmer} ${styles.skeletonBox}`} />
                      <div className={`${styles.shimmer} ${styles.skeletonBox}`} />
                    </div>
                  </div>
                </div>
                
                <div className={styles.skeletonPillarsWrapper}>
                   <div className={styles.skeletonPillarsGrid}>
                     {[1, 2, 3, 4].map(i => (
                       <div key={i} className={`${styles.shimmer} ${styles.skeletonPillarCard}`} />
                     ))}
                   </div>
                </div>
              </div>
            )}

            {/* ÉTAT 3 : COMPLETED */}
            {matchStatus === 'completed' && matchResult && (
              <div className={styles.statusSectionResult}>
                <div className={styles.resultHeaderRow}>
                  <div className={styles.successBadge}>
                    <CheckCircle2 size={18} strokeWidth={2} /> Analyse terminée
                  </div>
                  <div className={styles.resultActionsRow}>
                    <button onClick={resetMatching} className={styles.actionBtnLight}>
                      <RotateCcw size={14} strokeWidth={2} /> Fermer et tester un autre CV
                    </button>
                    <button onClick={() => navigate('/matching')} className={styles.actionBtnDark}>
                      <History size={14} strokeWidth={2} /> Voir dans l'historique complet
                    </button>
                  </div>
                </div>
                
                <div className={styles.matchingResultContainer}>
                  <MatchingResult data={matchResult} />
                </div>
              </div>
            )}

            {/* ÉTAT 4 : ERROR */}
            {matchStatus === 'error' && (
              <div className={styles.statusSection} style={{ textAlign: 'center', padding: '30px 15px' }}>
                <div className={styles.errorBadgeInline} style={{ margin: '0 auto 15px', display: 'inline-flex' }}>
                  <AlertTriangle size={20} strokeWidth={2} /> Erreur
                </div>
                <h3 style={{ fontSize: '1.1rem', marginBottom: '10px', color: 'var(--color-text-main)' }}>
                  Oups, l'analyse a été interrompue
                </h3>
                <div className={styles.errorBox} style={{ justifyContent: 'center', marginBottom: '20px', maxWidth: '500px', margin: '0 auto 20px', backgroundColor: 'transparent', border: 'none', color: 'var(--color-text-muted)' }}>
                  {matchError}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
                  <button onClick={resetMatching} className={styles.actionBtnLight} style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-main)' }}>
                    <RotateCcw size={15} strokeWidth={2} /> Réessayer
                  </button>
                  <a href="/support" className={styles.actionBtnLight} style={{ textDecoration: 'none', border: '1px solid var(--color-border)', background: 'transparent' }}>
                    <Mail size={15} strokeWidth={2} /> Contacter un admin
                  </a>
                </div>
              </div>
            )}
          </section>
        )}

        {/* ── Date ── */}
        {job.update_date && (
          <div className={styles.updateDate}>
            <CalendarDays size={13} strokeWidth={2} />
            Dernière mise à jour :{' '}
            {new Date(job.update_date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobDetailsPage;